import { supabase } from '../lib/supabase';

// Get all invoices for a client
export const getClientInvoices = async (clientId) => {
  try {
    const { data, error } = await supabase
      .from('invoices')
      .select('*')
      .eq('client_id', clientId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    
    // Add created_by_user object for compatibility (without relationship query)
    return (data || []).map(invoice => ({
      ...invoice,
      created_by_user: invoice.created_by ? { id: invoice.created_by, email: null } : null
    }));
  } catch (error) {
    console.error('Error fetching invoices:', error);
    throw error;
  }
};

// Get all invoices (for getting next invoice number)
export const getAllInvoices = async () => {
  try {
    const { data, error } = await supabase
      .from('invoices')
      .select('invoice_number')
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching all invoices:', error);
    throw error;
  }
};

// Get the next invoice number
export const getNextInvoiceNumber = async () => {
  try {
    const currentYear = new Date().getFullYear();
    const yearSuffix = currentYear.toString().slice(-2); // Get last 2 digits (26, 27, etc.)
    
    // Get all invoices to find the highest number for this year
    const invoices = await getAllInvoices();
    
    // Filter invoices for current year and extract numbers
    const currentYearInvoices = invoices
      .filter(inv => inv.invoice_number && inv.invoice_number.startsWith(yearSuffix + '-'))
      .map(inv => {
        const parts = inv.invoice_number.split('-');
        if (parts.length === 2) {
          return parseInt(parts[1], 10);
        }
        return 0;
      });
    
    // Find the highest number
    const maxNumber = currentYearInvoices.length > 0 
      ? Math.max(...currentYearInvoices) 
      : 0;
    
    // Generate next number
    const nextNumber = maxNumber + 1;
    const formattedNumber = nextNumber.toString().padStart(4, '0');
    
    return `${yearSuffix}-${formattedNumber}`;
  } catch (error) {
    console.error('Error getting next invoice number:', error);
    // Fallback to current year starting at 0001
    const currentYear = new Date().getFullYear();
    const yearSuffix = currentYear.toString().slice(-2);
    return `${yearSuffix}-0001`;
  }
};

// Get a single invoice
export const getInvoice = async (invoiceId) => {
  try {
    const { data, error } = await supabase
      .from('invoices')
      .select('*')
      .eq('id', invoiceId)
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error fetching invoice:', error);
    throw error;
  }
};

// Create a new invoice
export const createInvoice = async (invoiceData) => {
  try {
    const { data, error } = await supabase
      .from('invoices')
      .insert([invoiceData])
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error creating invoice:', error);
    throw error;
  }
};

// Update an invoice
export const updateInvoice = async (invoiceId, invoiceData) => {
  try {
    const { data, error } = await supabase
      .from('invoices')
      .update(invoiceData)
      .eq('id', invoiceId)
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error updating invoice:', error);
    throw error;
  }
};

// Toggle deposit collected status
export const toggleDepositCollected = async (invoiceId, depositCollected) => {
  try {
    const { data, error } = await supabase
      .from('invoices')
      .update({ deposit_collected: depositCollected })
      .eq('id', invoiceId)
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error toggling deposit collected:', error);
    throw error;
  }
};

// Delete an invoice
export const deleteInvoice = async (invoiceId) => {
  try {
    const { error } = await supabase
      .from('invoices')
      .delete()
      .eq('id', invoiceId);

    if (error) throw error;
  } catch (error) {
    console.error('Error deleting invoice:', error);
    throw error;
  }
};

// Get all unpaid invoices (across all clients)
export const getUnpaidInvoices = async () => {
  try {
    const { data, error } = await supabase
      .from('invoices')
      .select(`
        *,
        clients (
          id,
          name,
          email,
          phone
        )
      `)
      .eq('type', 'invoice')
      .neq('status', 'paid')
      .order('due_date', { ascending: true, nullsFirst: false })
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching unpaid invoices:', error);
    throw error;
  }
};

// Get unpaid invoices for a specific client
export const getUnpaidClientInvoices = async (clientId) => {
  try {
    const { data, error } = await supabase
      .from('invoices')
      .select('*')
      .eq('client_id', clientId)
      .eq('type', 'invoice')
      .neq('status', 'paid')
      .order('due_date', { ascending: true, nullsFirst: false })
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching unpaid client invoices:', error);
    throw error;
  }
};

// Mark invoice as paid
export const markInvoiceAsPaid = async (invoiceId) => {
  try {
    const { data, error } = await supabase
      .from('invoices')
      .update({ status: 'paid' })
      .eq('id', invoiceId)
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error marking invoice as paid:', error);
    throw error;
  }
};

// Get all open estimates (across all clients)
export const getOpenEstimates = async () => {
  try {
    const { data, error } = await supabase
      .from('invoices')
      .select(`
        *,
        clients (
          id,
          name,
          email,
          phone
        )
      `)
      .eq('type', 'estimate')
      .eq('status', 'draft')
      .order('created_at', { ascending: false });

    if (error) throw error;
    
    // Add created_by_user object for compatibility (without relationship query)
    return (data || []).map(estimate => ({
      ...estimate,
      created_by_user: estimate.created_by ? { id: estimate.created_by, email: null } : null
    }));
  } catch (error) {
    console.error('Error fetching open estimates:', error);
    throw error;
  }
};

// Get open estimates for a specific client
export const getOpenClientEstimates = async (clientId) => {
  try {
    const { data, error } = await supabase
      .from('invoices')
      .select('*')
      .eq('client_id', clientId)
      .eq('type', 'estimate')
      .eq('status', 'draft')
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching open client estimates:', error);
    throw error;
  }
};

// Get the most recent draft invoice/estimate for a client
// Only returns drafts that haven't been finalized (status='draft' and type='estimate' for estimates)
// Excludes finalized estimates (status='sent' or type='invoice')
export const getLatestDraft = async (clientId) => {
  try {
    const { data, error } = await supabase
      .from('invoices')
      .select('*')
      .eq('client_id', clientId)
      .eq('status', 'draft')
      .neq('type', 'invoice') // Exclude invoices (finalized estimates become invoices)
      .order('updated_at', { ascending: false })
      .limit(1)
      .maybeSingle(); // Use maybeSingle instead of single to handle no results gracefully

    if (error) throw error;
    return data || null;
  } catch (error) {
    console.error('Error fetching latest draft:', error);
    return null;
  }
};
